import { Link } from "react-router-dom";
import { CTAButton, Divider, Eyebrow, Reveal, SiteFooter } from "./primitives";
import { STORY_SECTIONS } from "./story";

const BackLink = () => (
  <header className="absolute top-0 left-0 w-full z-50 py-6 px-6">
    <div className="max-w-5xl mx-auto">
      <Link
        to="/"
        data-testid="story-back-link"
        className="font-sans text-[0.65rem] sm:text-xs tracking-[0.22em] uppercase text-[#C9A84C] hover:text-[#D4691E] transition-colors duration-300"
      >
        &larr; Back
      </Link>
    </div>
  </header>
);

const StoryHero = () => (
  <section
    id="story-hero"
    className="relative min-h-[70vh] w-full flex items-center justify-center overflow-hidden bg-[#0D1B2A]"
  >
    <div className="absolute inset-0 bg-[#0D1B2A]" aria-hidden="true">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_55%_at_50%_108%,rgba(184,84,30,0.28),rgba(212,105,30,0.12)_38%,transparent_72%)]" />
      <div className="absolute inset-x-0 top-0 h-3/5 bg-gradient-to-b from-[#0D1B2A] via-[#0D1B2A]/70 to-transparent" />
    </div>

    <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
      <Reveal>
        <h1 className="font-serif font-medium uppercase text-[#C9A84C] leading-[0.98] tracking-tight text-5xl sm:text-6xl md:text-7xl">
          The Full Story
        </h1>
        <p className="mt-5 font-sans text-[0.7rem] tracking-[0.24em] uppercase text-[#F5F0E8]/55">
          (written in 2019 before A.I)
        </p>
      </Reveal>
      <Reveal delay={0.15}>
        <p className="mt-8 font-serif italic text-xl sm:text-2xl md:text-3xl text-[#F5F0E8] leading-snug">
          "In the cave you fear to enter, lies the treasure you seek."
        </p>
        <p className="mt-4 font-sans text-[0.7rem] tracking-[0.24em] uppercase text-[#C9A84C]">
          — Joseph Campbell
        </p>
      </Reveal>
    </div>
  </section>
);

const Block = ({ block, dark }) => {
  const bodyColor = dark ? "text-[#F5F0E8]/90" : "text-[#1A2535]/85";

  if (block.type === "lines") {
    return (
      <Reveal>
        <p
          className={`mt-8 font-sans font-light text-base sm:text-lg leading-[1.9] ${bodyColor}`}
        >
          {block.items.map((line, i) => (
            <span key={i}>
              {line}
              {i < block.items.length - 1 && <br />}
            </span>
          ))}
        </p>
      </Reveal>
    );
  }

  if (block.type === "pull") {
    return (
      <Reveal>
        <p className="my-16 text-center font-serif italic text-2xl sm:text-3xl md:text-4xl leading-snug text-transparent bg-clip-text bg-gradient-to-r from-[#C9A84C] via-[#D4691E] to-[#B8541E]">
          {block.text}
        </p>
      </Reveal>
    );
  }

  if (block.type === "hinge") {
    return (
      <Reveal>
        <p
          className={`my-24 text-center font-serif italic text-2xl sm:text-3xl md:text-4xl leading-snug ${
            dark ? "text-[#C9A84C]" : "text-[#1A2535]"
          }`}
        >
          {block.text}
        </p>
      </Reveal>
    );
  }

  // default paragraph
  const content = block.underline
    ? (() => {
        const parts = block.text.split(block.underline);
        return (
          <>
            {parts[0]}
            <span className="underline decoration-[#C9A84C]/70 underline-offset-4">
              {block.underline}
            </span>
            {parts[1]}
          </>
        );
      })()
    : block.text;

  return (
    <Reveal>
      <p
        className={`mt-8 font-sans font-light text-base sm:text-lg leading-relaxed ${bodyColor}`}
      >
        {content}
      </p>
    </Reveal>
  );
};

const StorySection = ({ section, index }) => {
  const dark = section.variant === "dark";
  const bg = dark
    ? "bg-[#0D1B2A] text-[#F5F0E8]"
    : "bg-[#F0EAE0] text-[#1A2535]";
  return (
    <section
      className={`relative w-full px-6 py-20 sm:py-28 ${bg}`}
      data-testid={`story-section-${index}`}
    >
      {/* first section continues the hero's ember bleed */}
      {index === 0 && (
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-1/2 pointer-events-none bg-[radial-gradient(ellipse_90%_70%_at_50%_-20%,rgba(184,84,30,0.16),transparent_65%)]"
        />
      )}
      <div className="relative max-w-2xl mx-auto">
        {index > 0 && <Divider className="!mt-0 !mb-14" />}
        {section.blocks.map((block, i) => (
          <Block key={i} block={block} dark={dark} />
        ))}
      </div>
    </section>
  );
};

const StoryPage = () => {
  return (
    <div className="bg-[#0D1B2A] font-sans antialiased selection:bg-[#C9A84C]/30 selection:text-[#F5F0E8]">
      <BackLink />
      <StoryHero />

      {STORY_SECTIONS.map((section, i) => (
        <StorySection key={i} section={section} index={i} />
      ))}

      {/* Closing CTA */}
      <section
        className="relative w-full px-6 py-24 sm:py-32 bg-[#0D1B2A] text-[#F5F0E8]"
        data-testid="story-cta"
      >
        <div className="max-w-2xl mx-auto text-center">
          <Reveal>
            <Eyebrow>If This Resonated</Eyebrow>
            <Divider />
            <div className="mt-12">
              <CTAButton testId="story-cta-button" ember>
                Book an Alignment Call
              </CTAButton>
            </div>
          </Reveal>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default StoryPage;
